import React, { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import FormGenerator from '@sharedCR/common/formGenerator';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  DefaultLoanActionFieldValueData,
  FieldTypesEnum,
  LoanActionDateTypesEnum,
  LoanActionStaticValueSourceEnum,
} from 'constants/loanActionEditor';
import { deleteByIndex, setIn } from 'utils/data';

import { selectLoanActionFieldsMap } from 'store/system/selectors';

import useFormContext from './hooks/useFormContext';
import {
  getDefaultConstantFieldValue,
  getFormConfigForLoanActionConstantField,
} from './LoanActionsGroup.utils';
import SequenceFormatPreview from './SequenceFormatPreview';

const LoanActionConstantFieldRow = ({
  index,
  rootPath,
  options,
  initOptions,
  isDisabled = false,
  isEditAllowed,
  isSingleItem,
}) => {
  const fieldPath = `${rootPath}[${index}]`;

  const loanActionFieldsMap = useSelector(selectLoanActionFieldsMap);
  const {
    form,
    setInForm,
    setMultipleInForm,
    validateField,
    validationErrors,
    handleChange,
    setValidationErrors,
  } = useFormContext();

  const fields = _.get(form, rootPath);
  const targetFieldId = _.get(form, `${fieldPath}.field.value`);
  const dateType = _.get(form, `${fieldPath}.dateType`);
  const valueSource = _.get(form, `${fieldPath}.valueSource`);
  const digitCount = _.get(form, `${fieldPath}.digitCount`);
  const seed = _.get(form, `${fieldPath}.seed`);
  const targetTypeData = loanActionFieldsMap[targetFieldId] || DefaultLoanActionFieldValueData;

  const isNextSequence = valueSource === LoanActionStaticValueSourceEnum.NEXT_SEQUENCE;

  const resetErrorsForRow = useCallback(() => {
    setValidationErrors(errors =>
      setIn(errors, rootPath, deleteByIndex(_.get(errors, rootPath, []), index)),
    );
  }, [setValidationErrors, index, rootPath]);

  const onDeleteRow = useCallback(() => {
    resetErrorsForRow();
    setInForm(rootPath, deleteByIndex(fields, index));
  }, [setInForm, rootPath, fields, index, resetErrorsForRow]);

  const configLoanActionForm = useMemo(
    () =>
      getFormConfigForLoanActionConstantField({
        fieldPath,
        options,
        initOptions,
        valueSource,
        dateType,
        targetFieldId,
        targetTypeData,
        isDisabled: !isEditAllowed,
        onDeleteRow: isEditAllowed && !isSingleItem ? onDeleteRow : undefined,
      }),
    [
      fieldPath,
      options,
      initOptions,
      valueSource,
      dateType,
      targetFieldId,
      targetTypeData,
      isEditAllowed,
      isSingleItem,
      onDeleteRow,
    ],
  );

  const onSetInForm = useCallback(
    (name, value) => {
      if (name === `${fieldPath}.field`) {
        const fieldType = loanActionFieldsMap[value.value]?.type;

        resetErrorsForRow();
        setMultipleInForm({
          [`${fieldPath}.value`]: getDefaultConstantFieldValue(fieldType),
          [`${fieldPath}.sourceField`]: null,
          [`${fieldPath}.dateType`]:
            fieldType === FieldTypesEnum.DATE ? LoanActionDateTypesEnum.EXECUTION_DATE : null,
        });
      }

      if (
        name === `${fieldPath}.value` &&
        valueSource === LoanActionStaticValueSourceEnum.VALUE &&
        targetTypeData.type === FieldTypesEnum.DATE
      ) {
        setInForm(name, value ? dayjs(value) : value);

        return;
      }

      setInForm(name, value);
    },
    [
      fieldPath,
      targetTypeData.type,
      valueSource,
      setInForm,
      setMultipleInForm,
      loanActionFieldsMap,
      resetErrorsForRow,
    ],
  );

  const onChange = useCallback(
    event => {
      const { name } = event.target;

      handleChange(event);

      if (name === `${fieldPath}.dateType`) {
        resetErrorsForRow();
        setInForm(`${fieldPath}.value`, null);
        handleChange({ target: { name: `${fieldPath}.value`, value: null } });

        return;
      }

      if (name === `${fieldPath}.valueSource`) {
        resetErrorsForRow();
        setMultipleInForm({
          [`${fieldPath}.dateType`]:
            targetTypeData?.type === FieldTypesEnum.DATE
              ? LoanActionDateTypesEnum.EXECUTION_DATE
              : null,
          [`${fieldPath}.value`]: null,
          [`${fieldPath}.sourceField`]: null,
          [`${fieldPath}.sequenceKey`]: '',
          [`${fieldPath}.digitCount`]: '',
          [`${fieldPath}.seed`]: '',
        });
      }
    },
    [
      fieldPath,
      handleChange,
      resetErrorsForRow,
      setInForm,
      setMultipleInForm,
      targetTypeData?.type,
    ],
  );

  return (
    <>
      <FormGenerator
        isNestedForm
        config={configLoanActionForm}
        form={form}
        handleChange={onChange}
        isLoading={isDisabled}
        setInForm={onSetInForm}
        validateField={validateField}
        validationErrors={validationErrors}
      />
      {isNextSequence && (
        <SequenceFormatPreview
          digitCount={digitCount}
          name={`${fieldPath}.sequenceFormatPreview`}
          seed={seed}
        />
      )}
    </>
  );
};

LoanActionConstantFieldRow.propTypes = {
  index: PropTypes.number.isRequired,
  rootPath: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  initOptions: PropTypes.array.isRequired,
  isSingleItem: PropTypes.bool.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool,
};

export default memo(LoanActionConstantFieldRow);
